import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Waveform, Sparkle, ArrowRight, Copy, CheckCircle, Play, ShieldCheck,
  PaperPlaneTilt, EnvelopeSimple, Lightning, CircleNotch, FilmStrip
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const SAMPLE = `This works when you are moving off ACT, GoldMine, Salesforce or any other CRM and you want to keep every note linked to the right contact.
Once the file uploads the system parses the fields and matches them to your schema and flags the duplicates automatically.
SalesCrunchCRM costs thirty dollars a month.`;

export default function VoiceCorrectorPage() {
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [playing, setPlaying] = useState('');
  const [previewErr, setPreviewErr] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [suggestEmail, setSuggestEmail] = useState('');
  const [suggestSent, setSuggestSent] = useState(false);
  const [suggestErr, setSuggestErr] = useState('');

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Free AI Voiceover Corrector — VideoCrunch';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    meta?.setAttribute('content', 'Paste your voiceover script and get the same words back with human delivery — pauses, beats, emphasis. Works with ElevenLabs, OpenAI, Murf, any AI voice. Free.');
    return () => { document.title = prevTitle; if (prevDesc) meta?.setAttribute('content', prevDesc); };
  }, []);

  const correct = async () => {
    setLoading(true); setError(''); setResult(null); setCopied(false); setPreviewErr('');
    try {
      const { data } = await axios.post(`${API_URL}/api/voice/correct`, { script_text: script });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong — please try again.');
    }
    setLoading(false);
  };

  const correctedText = () => result ? result.lines.map(l => l.corrected).join('\n') : '';
  const originalText = () => result ? result.lines.map(l => l.original).join('\n') : '';

  const copyCorrected = async () => {
    await navigator.clipboard.writeText(correctedText());
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const preview = async (kind) => {
    setPreviewErr('');
    if (!EMAIL_RE.test(email.trim())) { setPreviewErr('Enter a valid email to unlock the audio preview.'); return; }
    setPlaying(kind);
    try {
      const text = (kind === 'before' ? originalText() : correctedText()).slice(0, 600);
      const { data } = await axios.post(`${API_URL}/api/voice/preview`, { text, kind, email: email.trim() });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_b64}`);
      audio.onended = () => setPlaying('');
      await audio.play();
    } catch (e) {
      setPreviewErr(e.response?.data?.detail || 'Preview failed — please try again.');
      setPlaying('');
    }
  };

  const submitSuggestion = async () => {
    setSuggestErr('');
    try {
      await axios.post(`${API_URL}/api/voice/suggestions`, { suggestion, email: suggestEmail });
      setSuggestSent(true); setSuggestion('');
    } catch (e) {
      setSuggestErr(e.response?.data?.detail || 'Could not send — please try again.');
    }
  };

  const changedLines = result?.lines.filter(l => l.changed || l.rejected) || [];

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white" data-testid="vc-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/voice" className="flex items-center gap-2" data-testid="vc-logo">
            <div className="w-8 h-8 bg-[#F39C12] rounded flex items-center justify-center">
              <FilmStrip size={18} weight="bold" className="text-slate-900" />
            </div>
            <span className="text-lg font-black tracking-tight">Video<span className="text-[#F39C12]">Crunch</span></span>
          </Link>
          <nav className="flex items-center gap-5 text-sm">
            <Link to="/tutorials" className="text-white/60 hover:text-white hidden sm:inline" data-testid="vc-nav-tutorials">See it in action</Link>
            <Link to="/" className="text-white/80 hover:text-white font-semibold" data-testid="vc-nav-salescrunch">SalesCrunchCRM →</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6">
        {/* Hero */}
        <section className="pt-16 pb-10 text-center">
          <p className="text-[#F39C12] text-xs font-black uppercase tracking-[0.2em] mb-3">Free tool — works with any AI voice</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
            Your AI voiceover sounds like a robot.<br />
            <span className="text-[#F39C12]">Same words. Human delivery.</span>
          </h1>
          <p className="text-sm text-white/70 max-w-2xl mx-auto leading-relaxed">
            Paste your script. Our cadence AI rewrites the <em>delivery</em> — breath marks, beats between
            list items, a pause before the payoff — using nothing but typography. Not a single word changes,
            and a machine proves it. Then paste the result into ElevenLabs, OpenAI, Murf... any voice tool you already use.
          </p>
        </section>

        {/* Corrector */}
        <section className="pb-12" data-testid="vc-corrector-section">
          <div className="bg-white/5 border border-white/15 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-bold flex items-center gap-2"><Waveform size={16} weight="bold" className="text-[#F39C12]" />Your voiceover script</label>
              <button onClick={() => setScript(SAMPLE)} className="text-xs text-[#F39C12] hover:text-amber-300 font-bold" data-testid="vc-sample-btn">Try a sample →</button>
            </div>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="Paste the script you feed your AI voice tool..."
              rows={7}
              maxLength={6000}
              className="w-full bg-black/30 border border-white/15 rounded-lg p-4 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60 resize-y"
              data-testid="vc-script-input"
            />
            <input type="text" tabIndex={-1} autoComplete="off" value="" onChange={() => {}} name="company_website" className="hidden" aria-hidden="true" />
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-white/40">{script.length}/6000 characters</span>
              <button
                onClick={correct}
                disabled={loading || script.trim().length < 10}
                className="inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-40 text-slate-900 font-black text-sm px-6 py-3 rounded-lg transition-colors"
                data-testid="vc-correct-btn"
              >
                {loading ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <Sparkle size={16} weight="bold" />}
                {loading ? 'Listening like a human...' : 'Correct my script'}
              </button>
            </div>
            {error && <div className="mt-3 bg-red-500/10 border border-red-500/30 text-red-300 px-3 py-2 text-sm rounded" data-testid="vc-error">{error}</div>}
          </div>

          {/* Results */}
          {result && (
            <div className="mt-8" data-testid="vc-results">
              <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="vc-summary">
                <span className="bg-[#F39C12]/15 border border-[#F39C12]/40 text-[#F39C12] text-xs font-black px-3 py-1.5 rounded-full">{result.summary.changed} line{result.summary.changed === 1 ? '' : 's'} improved</span>
                <span className="bg-white/5 border border-white/15 text-white/70 text-xs font-bold px-3 py-1.5 rounded-full">{result.summary.unchanged} already had good cadence</span>
                {result.summary.rejected > 0 && (
                  <span className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold px-3 py-1.5 rounded-full" data-testid="vc-rejected-chip">
                    <ShieldCheck size={13} weight="bold" />{result.summary.rejected} protected by the neutrality gate
                  </span>
                )}
              </div>

              {changedLines.length === 0 ? (
                <div className="bg-white/5 border border-white/15 rounded-xl p-6 text-center text-sm text-white/70" data-testid="vc-no-changes">
                  <CheckCircle size={24} weight="bold" className="text-emerald-400 mx-auto mb-2" />
                  Nice script — your cadence is already presenter-grade. Nothing to fix.
                </div>
              ) : (
                <div className="space-y-4">
                  {result.lines.map((l, i) => (l.changed || l.rejected) && (
                    <div key={i} className={`border rounded-xl p-4 ${l.rejected ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-white/5 border-white/15'}`} data-testid={`vc-line-${i}`}>
                      <p className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-1">Before</p>
                      <p className="text-sm text-white/60 leading-relaxed mb-3">{l.original}</p>
                      {!l.rejected && (<>
                        <p className="text-[10px] uppercase tracking-widest font-black text-[#F39C12] mb-1">After</p>
                        <p className="text-sm text-white leading-relaxed mb-3 font-medium">{l.corrected}</p>
                      </>)}
                      {l.why && <p className={`text-xs leading-relaxed ${l.rejected ? 'text-emerald-300' : 'text-white/50'}`}>{l.rejected ? '🛡 ' : '♪ '}{l.why}</p>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 mt-6">
                <button onClick={copyCorrected} className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-white/90 font-black text-sm px-5 py-2.5 rounded-lg transition-colors" data-testid="vc-copy-btn">
                  {copied ? <CheckCircle size={15} weight="bold" /> : <Copy size={15} weight="bold" />}
                  {copied ? 'Copied!' : 'Copy corrected script'}
                </button>
                <span className="text-xs text-white/40">Paste it into any AI voice tool — the typography does the rest.</span>
              </div>

              {/* Audio preview — email gated */}
              <div className="mt-8 bg-white/5 border border-white/15 rounded-xl p-6" data-testid="vc-preview-section">
                <h3 className="text-base font-bold mb-1 flex items-center gap-2"><Play size={16} weight="fill" className="text-[#F39C12]" />Hear the difference</h3>
                <p className="text-xs text-white/50 mb-4">Same AI voice, before and after. Drop your email to unlock the preview.</p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <EnvelopeSimple size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="bg-black/30 border border-white/15 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60 w-56"
                      data-testid="vc-preview-email"
                    />
                  </div>
                  <button onClick={() => preview('before')} disabled={!!playing}
                    className="inline-flex items-center gap-2 border border-white/25 hover:border-white/50 disabled:opacity-40 text-white/80 font-bold text-sm px-4 py-2.5 rounded-lg transition-colors"
                    data-testid="vc-preview-before-btn">
                    {playing === 'before' ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="fill" />}Before
                  </button>
                  <button onClick={() => preview('after')} disabled={!!playing}
                    className="inline-flex items-center gap-2 bg-[#F39C12]/15 border border-[#F39C12]/50 hover:bg-[#F39C12]/25 disabled:opacity-40 text-[#F39C12] font-bold text-sm px-4 py-2.5 rounded-lg transition-colors"
                    data-testid="vc-preview-after-btn">
                    {playing === 'after' ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} weight="fill" />}After
                  </button>
                </div>
                {previewErr && <p className="mt-3 text-xs text-red-300" data-testid="vc-preview-error">{previewErr}</p>}
              </div>
            </div>
          )}
        </section>

        {/* How it works */}
        <section className="py-12 border-t border-white/10" data-testid="vc-how-section">
          <h2 className="text-lg font-bold text-center mb-8">How it works</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', t: 'Paste your script', d: 'The exact text you feed ElevenLabs, OpenAI, Murf, or any other AI voice.' },
              { n: '2', t: 'The cadence AI re-punctuates the delivery', d: 'Beats between list names... a pause before the payoff — one punch word, maximum. Rules mined from producing 26 real tutorial videos.' },
              { n: '3', t: 'Copy it into any voice tool', d: 'The output is plain text with smarter typography. No tags, no markup — it works everywhere.' },
            ].map(s => (
              <div key={s.n} className="bg-white/5 border border-white/15 rounded-xl p-5">
                <div className="w-8 h-8 rounded-full bg-[#F39C12] text-slate-900 font-black text-sm flex items-center justify-center mb-3">{s.n}</div>
                <h3 className="text-sm font-bold mb-1.5">{s.t}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 bg-emerald-500/5 border border-emerald-500/25 rounded-xl p-4 max-w-2xl mx-auto">
            <ShieldCheck size={20} weight="bold" className="text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-xs text-white/70 leading-relaxed">
              <strong className="text-emerald-300">The neutrality gate:</strong> every corrected line is machine-compared
              against your original. If the AI changes even one word, that line is rejected and your original is kept.
              Your words are untouchable — only the delivery changes.
            </p>
          </div>
        </section>

        {/* Suggestion box */}
        <section className="py-12 border-t border-white/10" data-testid="vc-suggestion-section">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-lg font-bold mb-1 flex items-center gap-2"><Lightning size={18} weight="bold" className="text-[#F39C12]" />Help write the rulebook</h2>
            <p className="text-xs text-white/50 mb-4">
              Noticed something AI voices always get wrong? Tell us. Approved suggestions become permanent
              rules — every future correction gets better because of you.
            </p>
            {suggestSent ? (
              <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl p-4 text-sm flex items-center gap-2" data-testid="vc-suggestion-done">
                <CheckCircle size={18} weight="bold" />Got it — thank you! Every rule you send makes the corrector smarter.
              </div>
            ) : (
              <div className="bg-white/5 border border-white/15 rounded-xl p-5">
                <textarea
                  value={suggestion} onChange={(e) => setSuggestion(e.target.value)}
                  placeholder='e.g. "The voice always rushes phone numbers — it should pause between each group of digits."'
                  rows={3} maxLength={1000}
                  className="w-full bg-black/30 border border-white/15 rounded-lg p-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60 resize-y"
                  data-testid="vc-suggestion-input"
                />
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <input
                    type="email" value={suggestEmail} onChange={(e) => setSuggestEmail(e.target.value)}
                    placeholder="Email (optional — for credit)"
                    className="bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60 w-64"
                    data-testid="vc-suggestion-email"
                  />
                  <button onClick={submitSuggestion} disabled={suggestion.trim().length < 5}
                    className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-white/90 disabled:opacity-40 font-black text-sm px-5 py-2 rounded-lg transition-colors"
                    data-testid="vc-suggestion-submit">
                    <PaperPlaneTilt size={14} weight="bold" />Send it in
                  </button>
                </div>
                {suggestErr && <p className="mt-2 text-xs text-red-300">{suggestErr}</p>}
              </div>
            )}
          </div>
        </section>

        {/* Footer CTA */}
        <section className="py-12 border-t border-white/10 text-center">
          <p className="text-xs text-white/40 mb-4">
            A <Link to="/videocrunch" className="text-white/70 font-bold hover:text-white underline decoration-white/30">VideoCrunch</Link> tool — the AI video engine that produced all 26 SalesCrunchCRM tutorial videos. No camera. No actor. No editing.
          </p>
          <Link to="/tutorials" className="inline-flex items-center gap-2 text-[#F39C12] hover:text-amber-300 text-sm font-bold" data-testid="vc-footer-tutorials-link">
            Watch what VideoCrunch makes <ArrowRight size={14} weight="bold" />
          </Link>
        </section>
      </main>
    </div>
  );
}
